import { resolve } from "path";

import { registerPlatformSleepChecker, registerUsageSnapshotCron } from "./server-platform-maintenance.js";
import { registerPlatformAppProxy } from "./server-platform-app-proxy.js";
import { bootstrapPlatformAutoRecovery } from "./server-platform-auto-recovery.js";
import { registerPlatformCronScheduler } from "./server-platform-cron-scheduler.js";
import { registerScheduledJobsPoller, registerScheduledJobRoutes } from "./server-scheduled-jobs-runtime.js";
import { FUNCTION_TIMEOUTS } from "./server-execution.js";
import { registerWakeSignalDiagnosticsRoute } from "./server-wake-signal-diagnostics-route.js";
import { getPlatformWorkerDiagnostics } from "./server-platform-worker-diagnostics.js";
import { ensurePlatformRuntimeStateTable, getPlatformSlotActivity } from "./platform-active-slot.js";
import { createPostgresWakeSignalRuntime } from "./wake-signal/postgres-wake-signal-runtime.js";
import { createDisabledWakeSignalDiagnostics } from "./wake-signal/wake-signal-diagnostics.js";
import type { WakeSignalBus } from "./wake-signal/postgres-wake-signal-bus.js";
import * as wsGateway from "./ws-gateway.js";
import { buildServerPlatformNodeRouteRecord } from "./server-platform-node-routing.js";
import {
  registerNodeProxyInternalRoutesForRuntime,
  registerPlatformNodeOpsForRuntime,
} from "./server-platform-node-routes-bootstrap.js";
import { createNodeProxyWsTunnelUpgradeResponse } from "./server-node-proxy-ws-tunnel.js";
import { recordNodeRouteMetric } from "./server-node-routing-metrics.js";

type RawSql = (query: string, params?: unknown[]) => Promise<unknown>;
type LoggerLike = Pick<Console, "log" | "warn" | "error">;
type CapacityGateway = {
  getCapacity(): unknown;
};
type RuntimeMetricsGateway = {
  getRuntimeMetrics(): unknown;
};
type ActiveWorkerCheck = () => boolean | Promise<boolean>;
type DynamicImport = (path: string) => Promise<Record<string, any>>;
type PlatformEmailWorkerEnv = {
  RESEND_API_KEY?: string;
  PLATFORM_EMAIL_FROM?: string;
  PLATFORM_EMAIL_WORKER_DISABLED?: string;
  PLATFORM_EMAIL_WORKER_INTERVAL_MS?: string;
  PLATFORM_EMAIL_WORKER_BATCH_SIZE?: string;
  PLATFORM_EMAIL_WORKER_MAX_ATTEMPTS?: string;
};
type PlatformWebhookWorkerEnv = {
  PLATFORM_WEBHOOK_WORKER_DISABLED?: string;
  PLATFORM_WEBHOOK_WORKER_INTERVAL_MS?: string;
  PLATFORM_WEBHOOK_WORKER_BATCH_SIZE?: string;
  PLATFORM_WEBHOOK_WORKER_MAX_ATTEMPTS?: string;
  PLATFORM_WEBHOOK_WORKER_TIMEOUT_MS?: string;
};
const REALTIME_NOTIFY_INTERNAL_ERROR = "Internal realtime notify failed";

export function createPlatformSubdomainRegex(platformDomain: string): RegExp {
  const escaped = platformDomain.replace(/\./g, "\\.");
  return new RegExp(`^([a-z0-9][a-z0-9-]*[a-z0-9]?)\\.${escaped}$`);
}

export function registerPlatformInternalMiddleware(
  app: any,
  gateway: Pick<typeof wsGateway, "isLocalInternalHost"> = wsGateway,
): void {
  app.use("/internal/*", async (c: any, next: () => Promise<unknown>) => {
    if (c.req.path === "/internal/domain/verify") {
      return next();
    }
    if (c.req.path.startsWith("/internal/node-proxy/") && c.req.header("X-Gencow-Node-Proxy") === "v1") {
      return next();
    }
    const host = c.req.header("host") || "";
    const remoteAddress = c.env?.ip && typeof c.env.ip.address === "string" ? c.env.ip.address : null;
    if (!gateway.isLocalInternalHost(host, remoteAddress)) {
      return c.json({ error: "Forbidden" }, 403);
    }
    return next();
  });
}

export function registerRealtimeNotifyRoute(params: {
  app: any;
  db: any;
  appsTable: any;
  eqOp: (left: unknown, right: unknown) => unknown;
  gateway: Pick<
    typeof wsGateway,
    | "authorizeRealtimeNotifyRequest"
    | "notifyEmit"
    | "notifyInvalidate"
    | "notifyQueryInvalidate"
    | "notifyQueryRevoke"
    | "notifyChannelPublish"
    | "notifyChannelInvalidate"
    | "notifyChannelRevoke"
    | "consumeRealtimeNotifyRateLimit"
  >;
  generateInternalToken: (appId: number, appName: string) => string;
  logger?: LoggerLike;
}): void {
  const logger = params.logger ?? console;
  params.app.post("/internal/realtime-notify", async (c: any) => {
    try {
      const body = (await c.req.json()) as {
        appName?: string;
        type?: string;
        queryKey?: unknown;
        queryKeys?: unknown;
        query?: unknown;
        subscriptionKey?: unknown;
        scopeHash?: unknown;
        channelKey?: unknown;
        eventId?: unknown;
        epoch?: unknown;
        reason?: unknown;
        data?: unknown;
      };
      if (!body.type) {
        return c.json({ error: "type required" }, 400);
      }

      const auth = await params.gateway.authorizeRealtimeNotifyRequest({
        appName: body.appName,
        token: c.req.header("X-Internal-Token") || "",
        findAppByName: async (appName: string) => {
          const rows = await params.db
            .select({ id: params.appsTable.id, name: params.appsTable.name })
            .from(params.appsTable)
            .where(params.eqOp(params.appsTable.name, appName))
            .limit(1);
          return rows[0] ?? null;
        },
        generateInternalToken: params.generateInternalToken,
      });
      if (!auth.ok) {
        return c.json({ error: auth.error }, auth.status);
      }
      const rateLimit = params.gateway.consumeRealtimeNotifyRateLimit?.(auth.appName);
      if (rateLimit && !rateLimit.ok) {
        return c.json(
          { error: "Realtime notify rate limit exceeded", retryAfterMs: rateLimit.retryAfterMs },
          429,
        );
      }

      if (body.type === "emit") {
        if (typeof body.queryKey !== "string" || body.queryKey.length === 0) {
          return c.json({ error: "queryKey required" }, 400);
        }
        const sent = params.gateway.notifyEmit(auth.appName, body.queryKey, body.data);
        return c.json({ ok: true, sent });
      }

      if (body.type === "invalidate") {
        const queryKeys =
          typeof body.queryKey === "string" && body.queryKey.length > 0
            ? [body.queryKey]
            : Array.isArray(body.queryKeys) &&
                body.queryKeys.every((key: unknown) => typeof key === "string" && key.length > 0)
              ? [...new Set(body.queryKeys as string[])]
              : null;
        if (!queryKeys) return c.json({ error: "queryKey required" }, 400);
        const sent = params.gateway.notifyInvalidate(auth.appName, queryKeys);
        return c.json({ ok: true, sent });
      }

      if (body.type === "query_invalidate") {
        if (typeof body.query !== "string" || body.query.length === 0) {
          return c.json({ error: "query required" }, 400);
        }
        if (typeof body.subscriptionKey !== "string" || body.subscriptionKey.length === 0) {
          return c.json({ error: "subscriptionKey required" }, 400);
        }
        if (
          typeof body.scopeHash !== "string" ||
          body.scopeHash.length === 0 ||
          body.scopeHash.length > 200
        ) {
          return c.json({ error: "scopeHash required" }, 400);
        }
        const eventId =
          typeof body.eventId === "string" && body.eventId.length > 0 ? body.eventId : undefined;
        const sent = params.gateway.notifyQueryInvalidate(
          auth.appName,
          body.query,
          body.subscriptionKey,
          eventId,
          body.scopeHash,
        );
        return c.json({ ok: true, sent });
      }

      if (body.type === "query_revoke") {
        if (typeof body.query !== "string" || body.query.length === 0) {
          return c.json({ error: "query required" }, 400);
        }
        if (typeof body.subscriptionKey !== "string" || body.subscriptionKey.length === 0) {
          return c.json({ error: "subscriptionKey required" }, 400);
        }
        if (
          typeof body.scopeHash !== "string" ||
          body.scopeHash.length === 0 ||
          body.scopeHash.length > 200
        ) {
          return c.json({ error: "scopeHash required" }, 400);
        }
        const epoch =
          body.epoch === undefined || body.epoch === null
            ? undefined
            : typeof body.epoch === "string" && body.epoch.length > 0 && body.epoch.length <= 200
              ? body.epoch
              : null;
        const reason =
          body.reason === undefined || body.reason === null
            ? undefined
            : typeof body.reason === "string" && body.reason.length > 0 && body.reason.length <= 200
              ? body.reason
              : null;
        if (epoch === null) return c.json({ error: "epoch must be a non-empty string" }, 400);
        if (reason === null) return c.json({ error: "reason must be a non-empty string" }, 400);
        const sent = params.gateway.notifyQueryRevoke(
          auth.appName,
          body.query,
          body.subscriptionKey,
          epoch,
          reason,
          body.scopeHash,
        );
        return c.json({ ok: true, sent });
      }

      if (body.type === "channel_publish") {
        if (typeof body.channelKey !== "string" || body.channelKey.length === 0) {
          return c.json({ error: "channelKey required" }, 400);
        }
        const eventId =
          typeof body.eventId === "string" && body.eventId.length > 0 ? body.eventId : undefined;
        const sent = params.gateway.notifyChannelPublish(auth.appName, body.channelKey, body.data, eventId);
        return c.json({ ok: true, sent });
      }

      if (body.type === "channel_invalidate") {
        if (typeof body.channelKey !== "string" || body.channelKey.length === 0) {
          return c.json({ error: "channelKey required" }, 400);
        }
        const sent = params.gateway.notifyChannelInvalidate(auth.appName, body.channelKey);
        return c.json({ ok: true, sent });
      }

      if (body.type === "channel_revoke") {
        if (typeof body.channelKey !== "string" || body.channelKey.length === 0) {
          return c.json({ error: "channelKey required" }, 400);
        }
        const epoch =
          body.epoch === undefined || body.epoch === null
            ? undefined
            : typeof body.epoch === "string" && body.epoch.length > 0 && body.epoch.length <= 200
              ? body.epoch
              : null;
        const reason =
          body.reason === undefined || body.reason === null
            ? undefined
            : typeof body.reason === "string" && body.reason.length > 0 && body.reason.length <= 200
              ? body.reason
              : null;
        if (epoch === null) return c.json({ error: "epoch must be a non-empty string" }, 400);
        if (reason === null) return c.json({ error: "reason must be a non-empty string" }, 400);
        const sent = params.gateway.notifyChannelRevoke(auth.appName, body.channelKey, epoch, reason);
        return c.json({ ok: true, sent });
      }

      return c.json({ error: `Unknown type: ${body.type}` }, 400);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      logger.error("[ws-gateway] notify error:", message);
      return c.json({ error: REALTIME_NOTIFY_INTERNAL_ERROR }, 500);
    }
  });
}

export function registerCapacityRoute(params: { app: any; gateway: CapacityGateway }): void {
  params.app.get("/internal/capacity", (c: any) => {
    return c.json(params.gateway.getCapacity());
  });
}

export function registerRuntimeMetricsRoute(params: { app: any; gateway: RuntimeMetricsGateway }): void {
  params.app.get("/internal/runtime-metrics", (c: any) => {
    return c.json(params.gateway.getRuntimeMetrics());
  });
}

export function createPlatformActiveSlotWorkerCheck(params: {
  rawSql: RawSql;
  workerName: "platform-email" | "platform-webhook";
  logger?: LoggerLike;
}): ActiveWorkerCheck {
  const logger = params.logger ?? console;
  let lastActiveState: boolean | null = null;
  return async () => {
    const activity = await getPlatformSlotActivity({ rawSql: params.rawSql, logger });
    if (!activity.active) {
      if (lastActiveState !== false) {
        logger.log(
          `[${params.workerName}] worker paused on inactive slot :${activity.currentPort} (active :${activity.activePort})`,
        );
      }
      lastActiveState = false;
      return false;
    }
    if (lastActiveState === false) {
      logger.log(`[${params.workerName}] worker resumed on active slot :${activity.currentPort ?? "unknown"}`);
    }
    lastActiveState = true;
    return true;
  };
}

export async function startPlatformEmailWorkerForRuntime(params: {
  functionsPath: string;
  db: any;
  env?: PlatformEmailWorkerEnv;
  wakeSignalBus?: WakeSignalBus;
  isActiveWorker?: ActiveWorkerCheck;
  logger?: LoggerLike;
  setIntervalImpl?: typeof setInterval;
  clearIntervalImpl?: typeof clearInterval;
}): Promise<unknown | null> {
  const logger = params.logger ?? console;
  const env = params.env ?? {};
  const { createPlatformEmailProvider, resolvePlatformEmailWorkerConfig, startPlatformEmailWorker } =
    await import(resolve(params.functionsPath, "email-worker.ts"));
  const config = resolvePlatformEmailWorkerConfig(env);

  if (config.disabled) {
    logger.log("[platform-email] worker disabled by PLATFORM_EMAIL_WORKER_DISABLED");
    return null;
  }
  if (!env.RESEND_API_KEY) {
    logger.warn("[platform-email] worker skipped: RESEND_API_KEY is not configured");
    return null;
  }

  return startPlatformEmailWorker({
    db: params.db,
    provider: createPlatformEmailProvider(env),
    from: config.from,
    intervalMs: config.intervalMs,
    batchSize: config.batchSize,
    maxAttempts: config.maxAttempts,
    logger,
    wakeSignalBus: params.wakeSignalBus,
    isActiveWorker: params.isActiveWorker,
    setIntervalImpl: params.setIntervalImpl,
    clearIntervalImpl: params.clearIntervalImpl,
  });
}

export async function startPlatformWebhookWorkerForRuntime(params: {
  functionsPath: string;
  db: any;
  env?: PlatformWebhookWorkerEnv;
  wakeSignalBus?: WakeSignalBus;
  isActiveWorker?: ActiveWorkerCheck;
  logger?: LoggerLike;
  fetchImpl?: typeof fetch;
  setIntervalImpl?: typeof setInterval;
  clearIntervalImpl?: typeof clearInterval;
}): Promise<unknown | null> {
  const logger = params.logger ?? console;
  const env = params.env ?? {};
  const {
    createPlatformWebhookProvider,
    resolvePlatformWebhookWorkerConfig,
    startPlatformWebhookWorker,
  } = await import(resolve(params.functionsPath, "webhook-worker.ts"));
  const config = resolvePlatformWebhookWorkerConfig(env);

  if (config.disabled) {
    logger.log("[platform-webhook] worker disabled by PLATFORM_WEBHOOK_WORKER_DISABLED");
    return null;
  }

  return startPlatformWebhookWorker({
    db: params.db,
    provider: createPlatformWebhookProvider(env, {
      fetchImpl: params.fetchImpl,
      timeoutMs: config.timeoutMs,
    }),
    intervalMs: config.intervalMs,
    batchSize: config.batchSize,
    maxAttempts: config.maxAttempts,
    logger,
    wakeSignalBus: params.wakeSignalBus,
    isActiveWorker: params.isActiveWorker,
    setIntervalImpl: params.setIntervalImpl,
    clearIntervalImpl: params.clearIntervalImpl,
  });
}

export async function startGatewayRealtimeUsageMeteringForRuntime(params: {
  functionsPath: string;
  db: any;
  logger: LoggerLike;
  setIntervalImpl?: typeof setInterval;
  importImpl?: DynamicImport;
}): Promise<void> {
  const importImpl = params.importImpl ?? ((path: string) => import(path));
  const mod = await importImpl(resolve(params.functionsPath, "../src/ws-gateway-usage-metering.ts"));
  mod.startGatewayRealtimeUsageMetering({
    getDb: () => params.db,
    logger: params.logger,
    setIntervalImpl: params.setIntervalImpl,
  });
}

export async function bootstrapPlatformRuntime(params: {
  app: any;
  db: any;
  rawSql: RawSql;
  analyticsRawSql?: RawSql;
  functionsPath: string;
  platformDomain: string;
  getBunServer: () => unknown;
  logger?: LoggerLike;
  fetchImpl?: typeof fetch;
  setIntervalImpl?: typeof setInterval;
  clearIntervalImpl?: typeof clearInterval;
  setTimeoutImpl?: typeof setTimeout;
  platformEmailEnv?: PlatformEmailWorkerEnv;
  platformWebhookEnv?: PlatformWebhookWorkerEnv;
  wakeSignalEnv?: Record<string, string | undefined>;
}): Promise<void> {
  const logger = params.logger ?? console;
  const fetchImpl = params.fetchImpl ?? fetch;
  const setIntervalImpl = params.setIntervalImpl ?? setInterval;
  const clearIntervalImpl = params.clearIntervalImpl ?? clearInterval;
  const setTimeoutImpl = params.setTimeoutImpl ?? setTimeout;
  const subdomainRegex = createPlatformSubdomainRegex(params.platformDomain);

  const { addProxyMetric } = await import(resolve(params.functionsPath, "../src/proxy-metrics.ts"));
  const { apps: appsTable, platformNodes } = await import(resolve(params.functionsPath, "schema.ts"));
  const { eq: eqOp, and: andOp, inArray: inArrayOp } = await import("drizzle-orm");
  const { createProxyRequestReplayPlan } = await import(
    resolve(params.functionsPath, "../src/proxy-request-replay.ts")
  );
  const { buildAppRuntimeUnavailableResult, ensureAppRuntimeReady } = await import(
    resolve(params.functionsPath, "../src/app-runtime-recovery.ts")
  );
  const {
    getAppPort: getLivePort,
    setAppPort,
    removeAppPort,
    removeAppPortIfEquals,
    wakeApp,
    provisionApp,
    setAppMeta,
    setLastAccess: markLastAccess,
    hasLiveRuntimeProcess,
    resolveLiveRuntimePort,
    reapWarmPoolStale,
    getWakeCapacitySnapshot,
    generateInternalToken,
  } = await import(resolve(params.functionsPath, "../src/provisioner.ts"));
  const { getRuntimeMetricsSnapshot, startRuntimeMetricsSampler } = await import("./runtime-metrics.js");
  const platformNodeId = process.env.GENCOW_NODE_ID?.trim() || null;
  const nodeProxyKeyId = process.env.GENCOW_NODE_PROXY_KEY_ID?.trim() || null;
  const nodeProxySecret = process.env.GENCOW_NODE_PROXY_SECRET?.trim() || null;
  const nodeProxyEnabled = Boolean(platformNodeId && nodeProxyKeyId && nodeProxySecret);
  const nodeProxyHttpClient = nodeProxyEnabled
    ? await import(resolve(params.functionsPath, "../src/node-proxy-http-client.ts"))
    : null;
  const nodeProxyInternalRoutes = nodeProxyEnabled
    ? await import(resolve(params.functionsPath, "../src/platform-internal-routes.ts"))
    : null;
  const nodeOpsRoutes = await import(resolve(params.functionsPath, "../src/platform-node-ops.ts")).catch(
    () => null,
  );

  registerPlatformAppProxy({
    app: params.app,
    platformDomain: params.platformDomain,
    subdomainRegex,
    getBunServer: params.getBunServer,
    async findActiveDomainApp(host: string) {
      const rows = await params.db
        .select({ name: appsTable.name })
        .from(appsTable)
        .where(andOp(eqOp(appsTable.customDomain, host), eqOp(appsTable.customDomainStatus, "active")));
      return rows[0]?.name ?? null;
    },
    async findAppRow(appName: string) {
      const rows = await params.db
        .select({
          id: appsTable.id,
          name: appsTable.name,
          port: appsTable.port,
          status: appsTable.status,
          dataDir: appsTable.dataDir,
          staticDeployedAt: appsTable.staticDeployedAt,
          hasBackend: appsTable.hasBackend,
          plan: appsTable.plan,
          customDomain: appsTable.customDomain,
          customDomainStatus: appsTable.customDomainStatus,
          nodeId: appsTable.nodeId,
        })
        .from(appsTable)
        .where(eqOp(appsTable.name, appName));
      return rows[0] ?? null;
    },
    currentNodeId: nodeProxyEnabled ? platformNodeId : null,
    loadAppRouteNode:
      nodeProxyEnabled && platformNodes
        ? async (nodeId: string) => {
            const rows = await params.db
              .select({
                id: platformNodes.id,
                status: platformNodes.status,
                internalOrigin: platformNodes.internalOrigin,
                lastHeartbeatAt: platformNodes.lastHeartbeatAt,
              })
              .from(platformNodes)
              .where(eqOp(platformNodes.id, nodeId))
              .limit(1);
            return rows[0] ? buildServerPlatformNodeRouteRecord(rows[0]) : null;
          }
        : undefined,
    proxyRemoteNodeRequest:
      nodeProxyEnabled && nodeProxyHttpClient
        ? ({ c, host, appName, nodeId, origin, body }) => {
            recordNodeRouteMetric({ protocol: "http", kind: "remote", appName });
            return nodeProxyHttpClient.proxyRemoteNodeHttpRequest({
              request: c.req.raw,
              originalHost: host,
              origin,
              sourceNode: platformNodeId!,
              targetNode: nodeId,
              keyId: nodeProxyKeyId!,
              secret: nodeProxySecret!,
              body,
            });
          }
        : undefined,
    proxyRemoteNodeWebSocket: nodeProxyEnabled
      ? async ({ c, host, appName, nodeId, origin }) => {
          recordNodeRouteMetric({ protocol: "ws", kind: "remote", appName });
          return createNodeProxyWsTunnelUpgradeResponse({
            server: params.getBunServer() as any,
            request: c.req.raw,
            originalHost: host,
            origin,
            sourceNode: platformNodeId!,
            targetNode: nodeId,
            keyId: nodeProxyKeyId!,
            secret: nodeProxySecret!,
          });
        }
      : undefined,
    async markAppAsCrashed(appName: string) {
      await params.db.update(appsTable).set({ status: "crashed" }).where(eqOp(appsTable.name, appName));
      logger.log(`[platform] ${appName}: runtime unreachable → DB status set to "crashed"`);
    },
    async markHasBackend(appName: string) {
      await params.db.update(appsTable).set({ hasBackend: true }).where(eqOp(appsTable.name, appName));
      logger.log(`[platform] ${appName}: hasBackend corrected false → true`);
    },
    generateInternalToken,
    rawSql: params.rawSql,
    analyticsRawSql: params.analyticsRawSql ?? params.rawSql,
    functionsPath: params.functionsPath,
    addProxyMetric,
    createProxyRequestReplayPlan,
    buildAppRuntimeUnavailableResult,
    ensureAppRuntimeReady,
    getLivePort,
    setAppPort,
    removeAppPort,
    removeAppPortIfEquals,
    wakeApp,
    provisionApp,
    setAppMeta,
    markLastAccess,
    hasLiveRuntimeProcess,
    resolveLiveRuntimePort,
    reapRuntimeExits: reapWarmPoolStale,
    logger,
  });
  logger.log(`[platform] Subdomain proxy enabled for *.${process.env.BASE_DOMAIN || "gencow.app"}`);

  try {
    await bootstrapPlatformAutoRecovery({
      functionsPath: params.functionsPath,
      db: params.db,
      appsTable,
      eqOp,
      andOp,
      inArrayOp,
      currentNodeId: platformNodeId,
      logger,
      fetchImpl,
    });
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    logger.error("[platform] Auto-recovery failed:", message);
  }

  registerUsageSnapshotCron({
    platformPort: process.env.PORT || "4100",
    logger,
    fetchImpl,
    setIntervalImpl,
    setTimeoutImpl,
  });

  registerPlatformSleepChecker({
    functionsPath: params.functionsPath,
    fetchAppStatuses: async () =>
      (await params.db
        .select({ name: appsTable.name, status: appsTable.status, hasBackend: appsTable.hasBackend })
        .from(appsTable)) as Array<{ name: string; status: string; hasBackend: boolean }>,
    logger,
    setIntervalImpl,
  });

  await registerPlatformCronScheduler({
    functionsPath: params.functionsPath,
    rawSql: params.rawSql,
  });

  const wakeSignalRuntime = await createPostgresWakeSignalRuntime({
    rawSql: params.rawSql,
    env: params.wakeSignalEnv,
    logger,
  }).catch((error: unknown) => {
    const message = error instanceof Error ? error.message : String(error);
    logger.warn(`[wake-signal] PostgreSQL listener runtime disabled: ${message}`);
    return null;
  });

  await registerScheduledJobsPoller({
    functionsPath: params.functionsPath,
    rawSql: params.rawSql,
    cronExecTimeoutMs: FUNCTION_TIMEOUTS.cron,
    logger,
    wakeSignalBus: wakeSignalRuntime?.bus,
  });

  await ensurePlatformRuntimeStateTable(params.rawSql).catch((error: unknown) => {
    const message = error instanceof Error ? error.message : String(error);
    logger.warn(`[platform] worker active-slot table init failed: ${message}`);
  });

  try {
    await startPlatformEmailWorkerForRuntime({
      functionsPath: params.functionsPath,
      db: params.db,
      env: params.platformEmailEnv,
      wakeSignalBus: wakeSignalRuntime?.bus,
      isActiveWorker: createPlatformActiveSlotWorkerCheck({
        rawSql: params.rawSql,
        workerName: "platform-email",
        logger,
      }),
      logger,
      setIntervalImpl,
      clearIntervalImpl,
    });
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    logger.error("[platform-email] worker bootstrap failed:", message);
  }

  try {
    await startPlatformWebhookWorkerForRuntime({
      functionsPath: params.functionsPath,
      db: params.db,
      env: params.platformWebhookEnv,
      wakeSignalBus: wakeSignalRuntime?.bus,
      isActiveWorker: createPlatformActiveSlotWorkerCheck({
        rawSql: params.rawSql,
        workerName: "platform-webhook",
        logger,
      }),
      logger,
      fetchImpl,
      setIntervalImpl,
      clearIntervalImpl,
    });
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    logger.error("[platform-webhook] worker bootstrap failed:", message);
  }

  registerPlatformInternalMiddleware(params.app);
  registerCapacityRoute({ app: params.app, gateway: { getCapacity: getWakeCapacitySnapshot } });
  startRuntimeMetricsSampler();
  registerRuntimeMetricsRoute({ app: params.app, gateway: { getRuntimeMetrics: getRuntimeMetricsSnapshot } });
  registerWakeSignalDiagnosticsRoute({
    app: params.app,
    getDiagnostics: () => wakeSignalRuntime?.getDiagnostics() ?? createDisabledWakeSignalDiagnostics("disabled"),
    getWorkerDiagnostics: () => getPlatformWorkerDiagnostics({ functionsPath: params.functionsPath }),
  });
  registerPlatformNodeOpsForRuntime({
    nodeOpsRoutes,
    app: params.app,
    db: params.db,
    appsTable,
    platformNodes,
    eqOp,
    rawSql: params.rawSql,
    platformDomain: params.platformDomain,
    platformNodeId,
    getCapacity: getWakeCapacitySnapshot,
    getRuntimeMetrics: getRuntimeMetricsSnapshot,
    getLivePort,
    setIntervalImpl,
    clearIntervalImpl,
    logger,
  });

  registerRealtimeNotifyRoute({
    app: params.app,
    db: params.db,
    appsTable,
    eqOp,
    gateway: wsGateway,
    generateInternalToken,
    logger,
  });

  registerNodeProxyInternalRoutesForRuntime({
    nodeProxyEnabled,
    nodeProxyInternalRoutes,
    app: params.app,
    db: params.db,
    appsTable,
    eqOp,
    andOp,
    rawSql: params.rawSql,
    platformDomain: params.platformDomain,
    subdomainRegex,
    platformNodeId,
    nodeProxyKeyId,
    nodeProxySecret,
    generateInternalToken,
    getLivePort,
    logger,
  });

  registerScheduledJobRoutes({
    app: params.app,
    rawSql: params.rawSql,
    generateInternalToken,
    logger,
  });

  params.app.get("/internal/ws-gateway/stats", (c: any) => c.json(wsGateway.getStats()));
  params.app.get("/internal/warm-pool/stats", async (c: any) => {
    try {
      const { getWarmPoolStats } = await import(resolve(params.functionsPath, "../src/provisioner.ts"));
      return c.json(await getWarmPoolStats());
    } catch {
      return c.json({ enabled: false, error: "warm-pool not loaded" });
    }
  });

  try {
    await startGatewayRealtimeUsageMeteringForRuntime({
      functionsPath: params.functionsPath,
      db: params.db,
      logger,
      setIntervalImpl,
    });
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    logger.error("[ws-gateway] realtime usage metering bootstrap failed:", message);
  }

  wsGateway.startHeartbeat();
  const limits = wsGateway.getGatewayLimits();
  logger.log(
    `[ws-gateway] WS Gateway active (max ${limits.maxConnectionsPerApp}/app, ${limits.maxTotalConnections} total)`,
  );
}
